'use client';

import { ThemeProvider } from 'next-themes';
import { SessionProvider, AdminProvider } from '@paeffgen/react/crm-documents/feature-auth';
import { GraphQLProvider } from '@paeffgen/react/crm-documents/data-access-graphql';
import { CartProvider } from '@paeffgen/react/crm-documents/feature-material-requests';
import { NavHighlightProvider } from '@paeffgen/react/crm-documents/ui';
import type { ReactNode } from 'react';
import { ThemedToaster } from './themed-toaster';
import { SentryUserSync } from './sentry-user-sync';
import { SentryCrossLoginSync } from './sentry-cross-login-sync';

/**
 * Root providers for the CRM Documents Client.
 *
 * Wrapping order:
 * 0. ThemeProvider — next-themes (System-Auto + manueller Override, class-based)
 * 1. SessionProvider — next-auth session management
 *    └ SentryUserSync — spiegelt den eingeloggten User in den Sentry-Scope (rendert null)
 * 2. GraphQLProvider — Apollo Client with authenticated GraphQL + InMemoryCache
 * 3. AdminProvider — admin mode state (allCustomers toggle, customer filter)
 *    └ SentryCrossLoginSync — taggt den betrachteten Debitor im Sentry-Scope (rendert null)
 * 4. NavHighlightProvider — In-Memory state for nav "new" indicators
 * 5. CartProvider — Draft MaterialRequest state (cart persistence)
 *
 * ThemedToaster liegt im ThemeProvider, damit sonner dem aktiven Theme folgt.
 */
export function Providers({ children }: { children: ReactNode }) {
  return (
    <ThemeProvider attribute="class" defaultTheme="system" enableSystem disableTransitionOnChange>
      <SessionProvider>
        <SentryUserSync />
        <GraphQLProvider>
          <AdminProvider>
            <SentryCrossLoginSync />
            <NavHighlightProvider>
              <CartProvider>{children}</CartProvider>
            </NavHighlightProvider>
          </AdminProvider>
        </GraphQLProvider>
      </SessionProvider>
      <ThemedToaster />
    </ThemeProvider>
  );
}
